import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import InputCard from '../InputCard/InputCard';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { resolveIdleTimeouts } from '../Reader/Phrase/hooks/resolveIdleTimeouts';
import { HIGHLIGHT_DURATION } from '../../constants/assessmentScreenTimings';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { TriggerActions, triggerDataShallowEqual } from '../../constants/constants';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { logSessionInfo } from '../../services/logger';

const IDLE_ENCOURAGEMENT_TIMEOUT = 20000;
const IDLE_SUBMIT_TIMEOUT = 60000;

const TIME_BETWEEN_EXAMPLE_CLICKS = 800;
const TIME_AFTER_BACKSPACE_ANIMATION = 600;


const MAX_SELECTED_WORDS = 12;

const INSTRUCTION_PHASE_SUBMIT_NOOP = () => {};

// UI element names that this component knows how to show/hide/highlight/click
const UI_ELEMENTS = new Set(['repeat', 'input', 'options', 'backspace', 'submit']);

// Tiles are rendered in a fixed 4-column grid; rows wrap naturally.
const TILE_COLUMNS = 4;

const SentenceBuilder = ({
  speech,
  writtenInstructions,
  wordDisplay,
  isInstruction,
  isRepeating,
  setTextResponse,
  handleRepeatInstructions,
  repeatInstructions,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
  idleEncouragementText,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
}) => {
  // State
  const [selectedWords, setSelectedWords] = useState([]);
  const [hideOptions, setHideOptions] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [hideInputArea, setHideInputArea] = useState(false);
  const [hideRepeat, setHideRepeat] = useState(false);
  const [inFTUE, setInFTUE] = useState(false);
  const [pointerTileIndex, setPointerTileIndex] = useState(null);
  const [pointerTarget, setPointerTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPlayedIdleEncouragement, setHasPlayedIdleEncouragement] = useState(false);

  /** Blocks double final setTextResponse(..., true) if idle + submit race before loading/submitted state commits (mirror MMC submissionDispatchedRef). */
  const responseSentRef = useRef(false);

  const prevTriggerRef = useRef(null);
  const prevTriggerDataRef = useRef(null);
  const isMountedRef = useIsMounted();

  // wordDisplay is a string of tile tokens (mirrors Spelling's letterDisplay). Tokens are split
  // on commas and/or whitespace, so backends can use either delimiter (or both).
  // During FTUE testlet-level instructions this is example data; during items it's the real item data.
  // Phrase.js carry-through preserves whichever is set first.
  const flatWords = useMemo(() => {
    if (!wordDisplay || typeof wordDisplay !== 'string') return [];
    return wordDisplay.split(/[,\s]+/).map(w => w.trim()).filter(Boolean);
  }, [wordDisplay]);

  // Idle timer (mirror MMC: Reader repeat copy when meta is flattened)
  const encouragementText =
    idleEncouragementText || repeatInstructions || COPY_STRINGS.sentence_builder_idle_encouragement;

  // ASSMNT-2005: writtenCommunication items get the per-testlet-type idle/full timeout
  // (idleTimeoutOverrides, resolved in Phrase.js from testlet.type); no-override case falls back below.
  const { idleEncouragementTimeout: extendedIdleEncouragementTimeout, idleSubmitTimeout: extendedSubmitTimeout } = resolveIdleTimeouts(
    idleTimeoutOverrides,
    { idleEncouragementTimeout: IDLE_ENCOURAGEMENT_TIMEOUT, idleSubmitTimeout: IDLE_SUBMIT_TIMEOUT },
    shouldExtendTimeout,
    timeoutExtensionMultiplier
  );

  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech,
    extendedIdleEncouragementTimeout,
    extendedSubmitTimeout
  );

  // Computed
  const disableAll = useMemo(() => {
    return isInstruction || isRepeating || loading || idleSpeechPlaying;
  }, [isInstruction, isRepeating, loading, idleSpeechPlaying]);

  const disableSubmit = useMemo(() => {
    return disableAll || selectedWords.length < 1;
  }, [disableAll, selectedWords]);

  const sentenceText = useMemo(() => {
    return selectedWords.join(' ');
  }, [selectedWords]);

  const speechRef = useRef(speech);
  speechRef.current = speech;
  const sentenceTextRef = useRef(sentenceText);
  sentenceTextRef.current = sentenceText;
  const setTextResponseRef = useRef(setTextResponse);
  setTextResponseRef.current = setTextResponse;
  const clearIdleTimerRef = useRef(clearIdleTimer);
  clearIdleTimerRef.current = clearIdleTimer;
  const onTriggerActionCompleteRef = useRef(onTriggerActionComplete);
  onTriggerActionCompleteRef.current = onTriggerActionComplete;
  /** Reader passes `setHideRepeatButton.bind(this)` — new identity every render; do not list in effect deps. */
  const setHideParentRepeatButtonRef = useRef(setHideParentRepeatButton);
  setHideParentRepeatButtonRef.current = setHideParentRepeatButton;
  const animationGenRef = useRef(0);
  const ftueTriggerAnimationActiveRef = useRef(false);

  useEffect(() => {
    return () => {
      animationGenRef.current += 1;
      if (ftueTriggerAnimationActiveRef.current) {
        ftueTriggerAnimationActiveRef.current = false;
        onTriggerActionCompleteRef.current?.();
      }
    };
  }, []);

  // --- Handlers ---

  const handleWordClick = useCallback((word) => {
    if (inFTUE || disableAll) return;

    setSelectedWords((prev) => {
      if (prev.length >= MAX_SELECTED_WORDS) return prev;
      return [...prev, word];
    });
    if (!hasPlayedIdleEncouragement) {
      resetIdleTimer(encouragementText);
    }
  }, [inFTUE, disableAll, hasPlayedIdleEncouragement, encouragementText, resetIdleTimer]);

  const handleBackspace = useCallback(() => {
    if (inFTUE || disableAll) return;
    setSelectedWords((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
    if (!hasPlayedIdleEncouragement) {
      resetIdleTimer(encouragementText);
    }
  }, [inFTUE, disableAll, hasPlayedIdleEncouragement, encouragementText, resetIdleTimer]);

  const handleSubmit = useCallback(async (forceSubmit) => {
    if (responseSentRef.current) return;
    if ((inFTUE || disableSubmit) && !forceSubmit) return;

    const response = sentenceText || '';
    // forceSubmit (idle timeout): submit '' if empty, same contract as idle effect + sentenceTextRef.
    if (response || forceSubmit) {
      responseSentRef.current = true;
      setHasPlayedIdleEncouragement(false);
      clearIdleTimer();
      speech.abortSpeech();
      setLoading(true);

      if (forceSubmit) {
        await speech.speakWithPromise({
          textToSpeak: COPY_STRINGS.lets_move_on,
        }).catch((err) => {
          logSessionInfo('sentence_builder_idle_submit_speech_failed', {
            message: err?.message,
          });
        });
      }

      setTextResponse(response.toLowerCase(), true);
    }
  }, [inFTUE, disableSubmit, sentenceText, clearIdleTimer, speech, setTextResponse]);

  // --- Modeling / Trigger animations ---

  const animateClickWords = useCallback(async (words) => {
    if (!words || words.length === 0) {
      if (isMountedRef.current) {
        onTriggerActionCompleteRef.current?.();
      }
      return;
    }

    const gen = ++animationGenRef.current;
    ftueTriggerAnimationActiveRef.current = true;

    let nextSearchIndex = 0;
    for (const word of words) {
      let tileIdx = flatWords.indexOf(word, nextSearchIndex);
      if (tileIdx < 0) {
        tileIdx = flatWords.indexOf(word);
      }
      if (tileIdx >= 0) {
        nextSearchIndex = tileIdx + 1;
      }
      setPointerTileIndex(tileIdx >= 0 ? tileIdx : null);
      await new Promise((resolve) => {
        setTimeout(() => {
          if (!isMountedRef.current || animationGenRef.current !== gen) {
            resolve();
            return;
          }
          setSelectedWords((prev) => [...prev, word]);
          resolve();
        }, TIME_BETWEEN_EXAMPLE_CLICKS);
      });
    }

    if (animationGenRef.current !== gen) {
      return;
    }
    ftueTriggerAnimationActiveRef.current = false;
    setPointerTileIndex(null);
    if (isMountedRef.current) {
      onTriggerActionCompleteRef.current?.();
    }
  }, [flatWords]);

  const animateBackspace = useCallback(async () => {
    const gen = ++animationGenRef.current;
    ftueTriggerAnimationActiveRef.current = true;
    setPointerTarget('backspace');
    await new Promise((resolve) => {
      setTimeout(() => {
        if (!isMountedRef.current || animationGenRef.current !== gen) {
          resolve();
          return;
        }
        setSelectedWords((prev) => {
          if (prev.length === 0) return prev;
          return prev.slice(0, -1);
        });
        resolve();
      }, TIME_AFTER_BACKSPACE_ANIMATION);
    });
    if (animationGenRef.current !== gen) {
      return;
    }
    ftueTriggerAnimationActiveRef.current = false;
    setPointerTarget(null);
    if (isMountedRef.current) {
      onTriggerActionCompleteRef.current?.();
    }
  }, []);

  // --- Trigger handler (action:target system) ---

  const showTargets = useCallback((targets) => {
    // Phrase treats [show:options] and [show:repeatOverlayInput] as awaitable (isAwaitableIntroductionTrigger).
    // SB has no MMC-style option TTS or repeat overlay — one microtask completion is the contract (MMC differs).
    let signalAwaitableShowComplete = false;
    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(false); break;
        case 'input': setHideInputArea(false); break;
        case 'options':
          setHideOptions(false);
          signalAwaitableShowComplete = true;
          break;
        case 'repeatOverlayInput':
          // Defensive: MMC-only overlay; if content misroutes here, unblock Phrase (no UI to show).
          signalAwaitableShowComplete = true;
          break;
        case 'submit': setHideSubmit(false); break;
        default: break;
      }
    }
    if (signalAwaitableShowComplete) {
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
    }
  }, []);

  const hideTargets = useCallback((targets) => {
    for (const t of targets) {
      switch (t) {
        case 'repeat': setHideRepeat(true); break;
        case 'input': setHideInputArea(true); break;
        case 'options':
          setHideOptions(true);
          setSelectedWords([]);
          break;
        case 'submit': setHideSubmit(true); break;
        default: break;
      }
    }
  }, []);

  // Highlight is non-awaitable: pointer shows immediately, speech continues,
  // pointer stays visible until the next trigger clears it.
  const highlightTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) return;
    if (UI_ELEMENTS.has(target)) {
      setPointerTarget(target);
    }
  }, []);

  const clickTargets = useCallback((targets) => {
    if (!targets || targets.length === 0) {
      onTriggerActionCompleteRef.current?.();
      return;
    }

    const first = targets[0];

    if (first === 'backspace') {
      animateBackspace();
      return;
    }

    if (UI_ELEMENTS.has(first)) {
      // Point at the UI element (submit, repeat, etc.)
      setPointerTarget(first);
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setPointerTarget(null);
        onTriggerActionCompleteRef.current?.();
      }, HIGHLIGHT_DURATION);
      return;
    }

    // Targets are word values — animate clicking them in the grid
    animateClickWords(targets);
  }, [animateClickWords, animateBackspace]);

  const handleTrigger = useCallback((action, data) => {
    // Clear pointer from any previous highlight — each trigger resets it.
    // Highlight will re-set it; click sets its own pointer during animation.
    setPointerTarget(null);
    setPointerTileIndex(null);

    switch (action) {
      case TriggerActions.Start:
        setHideOptions(true);
        setHideSubmit(true);
        setHideInputArea(true);
        setHideRepeat(true);
        setInFTUE(true);
        setSelectedWords([]);
        break;
      case TriggerActions.Ready:
        // Clear modeled words before leaving FTUE so they are not emitted as partial responses
        setSelectedWords([]);
        setInFTUE(false);
        break;
      case TriggerActions.Show:
        showTargets(data);
        break;
      case TriggerActions.Hide:
        hideTargets(data);
        break;
      case TriggerActions.Highlight:
        highlightTargets(data);
        break;
      case TriggerActions.Click:
        clickTargets(data);
        break;
      default:
        break;
    }
  }, [showTargets, hideTargets, highlightTargets, clickTargets]);

  // --- Effects ---

  useEffect(() => {
    setHideParentRepeatButtonRef.current?.(true);
    return () => {
      setHideParentRepeatButtonRef.current?.(false);
    };
  }, []);

  // Handle triggers from Phrase
  useEffect(() => {
    if (!currentTriggerAction) {
      prevTriggerRef.current = null;
      prevTriggerDataRef.current = null;
      return;
    }
    const data = currentTriggerData || [];
    const sameAction = currentTriggerAction === prevTriggerRef.current;
    const sameData = triggerDataShallowEqual(data, prevTriggerDataRef.current);
    if (sameAction && sameData) {
      return;
    }
    prevTriggerRef.current = currentTriggerAction;
    prevTriggerDataRef.current = [...data];
    handleTrigger(currentTriggerAction, data);
  }, [currentTriggerAction, currentTriggerData, handleTrigger]);

  // Idle timeout: force submit (deps match MMC — do not list handleSubmit; it changes when
  // disableSubmit/loading change and idleTimeoutExpired stays true, which re-fired this effect).
  useEffect(() => {
    if (!idleTimeoutExpired) return;
    if (isInstruction || isRepeating || inFTUE || loading) {
      return;
    }
    if (responseSentRef.current) return;
    responseSentRef.current = true;
    setHasPlayedIdleEncouragement(false);
    clearIdleTimerRef.current();
    speechRef.current.abortSpeech();
    setLoading(true);

    speechRef.current
      .speakWithPromise({
        textToSpeak: COPY_STRINGS.lets_move_on,
      })
      .then(() => {
        if (!isMountedRef.current) return;
        const response = sentenceTextRef.current || '';
        setTextResponseRef.current(response.toLowerCase(), true);
      })
      .catch((err) => {
        logSessionInfo('sentence_builder_idle_timeout_speech_failed', {
          message: err?.message,
        });
        if (!isMountedRef.current) return;
        const response = sentenceTextRef.current || '';
        setTextResponseRef.current(response.toLowerCase(), true);
      });
  }, [idleTimeoutExpired, isInstruction, isRepeating, inFTUE, loading]);

  // Track idle encouragement
  useEffect(() => {
    if (idleSpeechPlaying && !hasPlayedIdleEncouragement) {
      setHasPlayedIdleEncouragement(true);
    }
  }, [idleSpeechPlaying, hasPlayedIdleEncouragement]);

  // New instruction phase → allow a new final response (Phrase keys SB per item; this covers remount + flip).
  useEffect(() => {
    if (isInstruction) {
      responseSentRef.current = false;
    }
  }, [isInstruction]);

  // When instructions finish, reset visibility and enable interaction (mirrors MMC: deps [isInstruction] only
  // so isRepeating true→false mid-item does not wipe selectedWords).
  // encouragementText / resetIdleTimer omitted from deps on purpose (stable for typical item metadata).
  useEffect(() => {
    if (!isInstruction) {
      setHideOptions(false);
      setHideSubmit(false);
      setHideInputArea(false);
      setHideRepeat(false);
      setInFTUE(false);
      setSelectedWords([]);
      setLoading(false);
      if (!hasPlayedIdleEncouragement) {
        resetIdleTimer(encouragementText);
      }
    }
  }, [isInstruction]);

  // Send partial response as user builds sentence (ref avoids re-firing when parent passes new setTextResponse each render)
  useEffect(() => {
    if (selectedWords.length > 0 && !inFTUE) {
      setTextResponseRef.current(sentenceText.toLowerCase(), false);
    }
  }, [selectedWords, inFTUE, sentenceText]);

  // --- Render ---

  return (
    <div className="sentenceBuilder">
      {/* Repeat button — top-left of screen */}
      <div className={cx('sentenceBuilder__repeatBtn', { hidden: hideRepeat })}>
        <AmiraIconButton
          type={ICON_BUTTON_TYPES.REPEAT_AUDIO}
          secondary
          onClick={() => {
            if (!inFTUE && !disableAll && handleRepeatInstructions) {
              clearIdleTimer();
              handleRepeatInstructions();
            }
          }}
          disabled={disableAll && pointerTarget !== 'repeat'}
          pointerPosition="left"
          showPointer={pointerTarget === 'repeat'}
        />
      </div>

      {/* Question text */}
      {writtenInstructions && (
        <div className="sentenceBuilder__question">
          {writtenInstructions}
        </div>
      )}

      {/* Input area: built sentence with fill-in-the-blank look */}
      <InputCard
        hideRepeat
        onBackspace={handleBackspace}
        disableBackspace={(disableAll || selectedWords.length === 0) && pointerTarget !== 'backspace'}
        hidden={hideInputArea}
        showBackspacePointer={pointerTarget === 'backspace'}
      >
        <div className="sentenceBuilder__sentence">
          {selectedWords.length === 0 ? (
            <span className="sentenceBuilder__placeholder">&nbsp;</span>
          ) : (
            <div className="sentenceBuilder__words">
              {selectedWords.map((word, index) => (
                <span className="sentenceBuilder__wordChip" key={`${word}-${index}`}>
                  {word}
                </span>
              ))}
            </div>
          )}
        </div>
      </InputCard>

      {/* Word grid */}
      <div className={cx('sentenceBuilder__grid', { hidden: hideOptions })}>
        <div
          className="sentenceBuilder__gridInner"
          style={{ gridTemplateColumns: `repeat(${TILE_COLUMNS}, 1fr)` }}
        >
          {flatWords.map((word, index) => (
            <button
              className={cx('sentenceBuilder__tile', 'pointerTarget', 'bottom', {
                disabled: disableAll && !inFTUE && !isInstruction,
                showPointer: pointerTileIndex === index,
              })}
              key={`${word}-${index}`}
              onClick={() => handleWordClick(word)}
              disabled={disableAll}
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Submit button — during modeling (FTUE / instructions) we render the button
         as enabled so it looks interactive, but pass a no-op callback so clicks do
         nothing. We still disable it when the input is empty so the enabled/disabled
         transition mirrors real usage. Outside of modeling, normal disableSubmit applies. */}
      <AmiraIconButton
        type={ICON_BUTTON_TYPES.SUBMIT}
        oblong
        small
        onClick={inFTUE || isInstruction ? INSTRUCTION_PHASE_SUBMIT_NOOP : () => handleSubmit()}
        disabled={inFTUE || isInstruction ? selectedWords.length === 0 : disableSubmit}
        invisible={hideSubmit}
        pointerPosition="left"
        showPointer={pointerTarget === 'submit'}
      />
    </div>
  );
};

export default SentenceBuilder;
